import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, type IconName } from '../Icon.js';
import { useContactInteractions } from '../../hooks/useContacts.js';
import { Modal } from '../ui/Modal.js';
import { LoadingState } from '../ui/LoadingState.js';
import { EmptyState } from '../ui/EmptyState.js';
import { Button } from '../ui/Button.js';
import { parseServerDate } from '../../lib/date.js';

interface Props {
  contactId: number;
  contactName: string;
  isOpen: boolean;
  onClose: () => void;
}

const ICON_MAP: Record<string, IconName> = {
  email_received: 'inbox',
  email_sent:     'send',
  note_manual:    'compose',
  lead_created:   'bolt',
  tag_added:      'flag',
  category_changed: 'settings',
};

export function ContactInteractionsModal({ contactId, contactName, isOpen, onClose }: Props) {
  const { t } = useTranslation();
  const [limit, setLimit] = useState(50);
  const { data: interactions = [], isLoading } = useContactInteractions(
    isOpen ? contactId : null,
    { limit },
  );

  const relativeTime = (dateStr: string): string => {
    const diff = Date.now() - parseServerDate(dateStr).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 2) return t('common.timeAgo.justNow');
    if (minutes < 60) return t('common.timeAgo.minutes', { count: minutes });
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return t('common.timeAgo.hours', { count: hours });
    const days = Math.floor(hours / 24);
    return t('common.timeAgo.days', { count: days });
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={`${t('contacts.interactions.title')} — ${contactName}`}
      size="md"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {isLoading && <LoadingState size="sm" />}
        {!isLoading && interactions.length === 0 && (
          <EmptyState size="sm" reserveSpace title={t('contacts.interactions.no_records')} />
        )}
        {interactions.map(row => (
          <div
            key={row.id}
            className="flex gap-3 p-2.5 rounded-lg"
            style={{ background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)' }}
          >
            <div className="shrink-0 mt-0.5" style={{ color: 'var(--evo-fg-3)' }}>
              <Icon name={ICON_MAP[row.interactionType] ?? 'activity'} size={14} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                <span
                  className="px-1.5 py-0.5 rounded"
                  style={{
                    fontSize: '10px',
                    background: 'var(--evo-bg-3)', color: 'var(--evo-fg-3)',
                    border: '1px solid var(--evo-stroke)',
                  }}
                >
                  {t(`contacts.interactions.type.${row.interactionType}`, { defaultValue: row.interactionType })}
                </span>
                <span style={{ fontSize: '10px', color: 'var(--evo-fg-3)' }}>
                  {relativeTime(row.createdAt)} · {parseServerDate(row.createdAt).toLocaleDateString()}
                </span>
              </div>
              <p className="text-xs leading-relaxed" style={{ color: 'var(--evo-fg-2)', wordBreak: 'break-word' }}>
                {row.summaryText}
              </p>
            </div>
          </div>
        ))}
        {interactions.length >= limit && (
          <Button variant="link" tone="cyan" size="sm" onClick={() => setLimit(l => l + 50)}>
            {t('contacts.interactions.load_more')}
          </Button>
        )}
      </div>
    </Modal>
  );
}
